"use client"

import useScrollReveal from "../../hooks/useScrollReveal"
import "./HowToVote.css"

const HowToVote = ({ language }) => {
  useScrollReveal(".vote-step-item, .requirement-item")

  const content = {
    en: {
      title: "How to Vote in Kano State",
      subtitle: "INEC Voting Procedures and Guidelines",
      stepsTitle: "Voting Steps",
      requirementsTitle: "Voting Requirements",
      steps: [
        {
          number: "1",
          title: "Register as a Voter",
          description: "Visit your Local Government Area (LGA) INEC office or designated registration center. Provide valid identification (National ID, Passport, or Driver's License). Complete the voter registration form with your personal details and biometric data.",
          icon: "📋",
        },
        {
          number: "2",
          title: "Get Your Voter Card",
          description: "After registration, wait for INEC to process your data (usually 2-4 weeks). Collect your Permanent Voter Card (PVC) from your designated polling unit or registration center.",
          icon: "🎫",
        },
        {
          number: "3",
          title: "Know Your Polling Unit",
          description: "Check the INEC voters' register to find your assigned polling unit. Polling units are usually located in schools, community centers, or public buildings. Note the location and polling unit number.",
          icon: "📍",
        },
        {
          number: "4",
          title: "Arrive Early on Election Day",
          description: "Voting starts at 8:00 AM. Arrive early to avoid long queues. Bring your Permanent Voter Card (PVC) and a valid form of identification. Polls close at 4:00 PM.",
          icon: "⏰",
        },
        {
          number: "5",
          title: "Present Your Credentials",
          description: "Queue at your polling unit. Present your PVC and/or valid ID to the election officials. They will verify your details in the voters' register and confirm you haven't voted.",
          icon: "✓",
        },
        {
          number: "6",
          title: "Collect Your Ballot",
          description: "After verification, you will receive the ballot paper(s) for the election. The officials will mark your finger with indelible ink to prevent double voting.",
          icon: "🗳️",
        },
        {
          number: "7",
          title: "Vote Privately",
          description: "Go to a voting booth to mark your choice in private. Read the ballot carefully and mark your preferred candidate(s) with an X. Ensure your mark is clear and within the box.",
          icon: "🎯",
        },
        {
          number: "8",
          title: "Cast Your Vote",
          description: "Fold your ballot as instructed. Place it in the ballot box. Your vote is now cast and counted. Do not take photos or disclose your vote outside the polling unit.",
          icon: "💪",
        },
      ],
      requirements: [
        "Be a Nigerian citizen",
        "Be at least 18 years old",
        "Be of sound mind",
        "Be a resident of Kano State (for local elections)",
        "Be registered in the voters' register",
        "Have a valid Permanent Voter Card (PVC) or acceptable ID",
        "Not have been disqualified by law from voting",
      ],
    },
    ha: {
      title: "Yadda Za Ka Jefa Kura a Jihar Kano",
      subtitle: "Hanyoyin Jefa Kura Bisa ga INEC",
      stepsTitle: "Matakain Jefa Kura",
      requirementsTitle: "Abubuwan da Suka Deba",
      steps: [
        {
          number: "1",
          title: "Jajiya a Matsayin Mai-Kura",
          description: "Je kumburin INEC na LGA naka ko jerin jajiya. Bayar da nuni mai inganci (ID, Pasipot, ko Likitancin tuqi). Cika falin jajiya tare da bayanin jinsi da alakar yatso.",
          icon: "📋",
        },
        {
          number: "2",
          title: "Ɗauke Kadir Mai-Kura",
          description: "Bayan jajiya, jira domin INEC ta aiki bayansa (jiya 2-4 mako). Ɗauke Kadir Mai-Kura na Juju (PVC) daga jerin kura naka.",
          icon: "🎫",
        },
        {
          number: "3",
          title: "Sani Jerin Kura Naka",
          description: "Duba litaffin mai-kura na INEC don neman jerin kura naka. Jeri-jeri ana aiki a makarantu, garuruwan jama'a ko gidajen kasuwa. Tuna waje da lambar jeri.",
          icon: "📍",
        },
        {
          number: "4",
          title: "Zo Jiya Ranar Jefa Kura",
          description: "Jefar kura takan fara da karfe 8. Zo jiya domin karewa a jeri. Kai Kadir Mai-Kura da nuni. Maraye suna rufe karfe 4 da yamma.",
          icon: "⏰",
        },
        {
          number: "5",
          title: "Gabatar da Abubuwan Tabbatarwa",
          description: "Durowa jerin kura. Gabatar da PVC da nuni ga jama'ar zaben. Za su tabbatar bayanka a cikin jerin mai-kura.",
          icon: "✓",
        },
        {
          number: "6",
          title: "Karɓi Kasuwar Kura",
          description: "Bayan tabbatarwa, za ka karɓi takardun kura. Jama'ar zaben za su yi alama yatso da inki wanda ba zai fita ba.",
          icon: "🗳️",
        },
        {
          number: "7",
          title: "Jefa Kura a Asiri",
          description: "Je cikin ɗakin kura don yin alamar zaɓi naka. Karanta kasuwar kura sosai da yi alama da X. Tabbatar da alama ta kai yaya a cikin akwatin.",
          icon: "🎯",
        },
        {
          number: "8",
          title: "Zubar da Kura",
          description: "Kaita kasua kamar yadda aka furta. Saka cikin akwatin kura. Kura naka ya jefa kuma ana kirga. Ka yi karya ta allo ko bayyana kura naka.",
          icon: "💪",
        },
      ],
      requirements: [
        "Zama dan Nigerian",
        "Zama dan shekara 18 ko fiye",
        "Zama mai hankalin lokaci",
        "Zama mazaunin Kano (don zaɓen gida)",
        "Kasancewa a jerin mai-kura",
        "Kasancewa da Kadir Mai-Kura ko nuni mai inganci",
        "Ba kawoɗu daga jiya ta jefa kura ba",
      ],
    },
  }

  const current = content[language]

  return (
    <div className="how-to-vote">
      {/* Header */}
      <div className="vote-header">
        <h1>{current.title}</h1>
        <p>{current.subtitle}</p>
      </div>

      {/* Main Container */}
      <div className="vote-container">
        {/* Voting Steps Section */}
        <section className="voting-steps-section">
          <h2 className="section-title">{current.stepsTitle}</h2>
          <div className="steps-timeline">
            {current.steps.map((step, index) => (
              <div
                key={index}
                className="vote-step-item scroll-reveal slide-left"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="step-circle">{step.number}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Requirements Section */}
        <section className="requirements-section">
          <h2 className="section-title">{current.requirementsTitle}</h2>
          <div className="requirements-list">
            {current.requirements.map((req, index) => (
              <div
                key={index}
                className="requirement-item scroll-reveal slide-left"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className="requirement-check">✓</div>
                <p>{req}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default HowToVote
